import React, { useEffect, useMemo, useState } from "react";
import PropertySelect from "./PropertySelect";
import RecipientItem from "../CampaignSteps/components/RecipientItem";
import { GroupOption, PropertyOption } from "./Step3.types";
import { getGroupsByPropties } from "PhishingManager/service/index.Service";
import DebounceInput from "PhishingManager/components/DebouncedInput";


interface Props {
    loading: boolean;
    tenentId: number;
    propertyOptions: PropertyOption[];
    selectedGroups: GroupOption[];
    setSelectedGroups: (groups: GroupOption[]) => void;
    toggleLoading: (status: boolean) => void;
    selectedProperties: number[];
    setSelectedProperties: (propertyIds: number[]) => void;
}

const GroupRecipients: React.FC<Props> = ({
    tenentId,
    loading,
    propertyOptions,
    selectedGroups,
    setSelectedGroups,
    toggleLoading,
    selectedProperties,
    setSelectedProperties
}) => {
    const [search, setSearch] = useState("");
    const [groupOptions, setGroupOptions] = useState<GroupOption[]>([]);
    const [allSelected, setAllSelected] = useState(false);
    const [allGroupsSelected, setAllGroupsSelected] = useState(false);

    const filteredGroups = useMemo(() => {
        return groupOptions.filter((group) =>(allSelected || selectedProperties.includes(group.propertyId)) ||(search ? group.label.toLowerCase().includes(search.toLowerCase()) : group));
    }, [groupOptions, selectedProperties, allSelected, search]);

    const totalUsersCount = useMemo(() => groupOptions.reduce((acc, group) => {
        const isSelected = selectedGroups.find((s) => s.value === group.value);
        return isSelected ? acc + group.count : acc;
    }, 0), [filteredGroups, selectedGroups])

    const handleGroupToggle = (groupItem: GroupOption) => {
        const isSelected = selectedGroups.some((g) => g.value === groupItem.value);
    
        setSelectedGroups(isSelected ? selectedGroups.filter((g) => g.value !== groupItem.value) : [...selectedGroups, groupItem]);
    };

    const getGroups = async () => {
        try {
            if (!tenentId) return;
            toggleLoading(true);
            const res = await getGroupsByPropties(Number(tenentId), selectedProperties);
            const groups = (res?.data || []).map((group: any) => ({
                value: group.venza_group_id,
                label: group.group_name,
                count: group.user_count || 0,
                propertyId: group.property_id || 0
            }))
            setGroupOptions(groups);
        } catch (error) {
            setGroupOptions([]);
        } finally {
            toggleLoading(false);
        }
    };

    useEffect(() => {
        if (selectedProperties.length) getGroups();
        else setGroupOptions([]);
    }, [selectedProperties])

    useEffect((() => {
        if (!!allGroupsSelected) setSelectedGroups([...filteredGroups]);
        else setSelectedGroups([]);
    }), [allGroupsSelected]);

    useEffect((() => {
        if (!allSelected) setSelectedGroups([]);
    }), [allSelected]);

    return (
        <div className="phishing-step3__role-section">
            <div className="phishing-step3__role-filters">
                <div className="phishing-step3__filter-group">
                    <div className="phishing-step3__label">
                        Select property <span className="required">*</span>
                    </div>
                    <PropertySelect
                        loading={loading}
                        propertyOptions={propertyOptions}
                        selectedProperties={selectedProperties}
                        setSelectedProperties={setSelectedProperties}
                        allSelected={allSelected}
                        setAllSelected={setAllSelected}
                        onPropertyDeselect={(propertyValue) => {
                            setSelectedGroups(
                                selectedGroups.filter((group) => {
                                    const selGroup = filteredGroups.find((g) => g.value === group.value);
                                    return selGroup?.propertyId !== propertyValue;
                                })
                            );
                        }}
                    />
                </div>

                <div className="phishing-step3__filter-group phishing-step3__role-search">
                    <div>
                        <DebounceInput placeholder="Search groups" setDebouncedSearch={(value) => setSearch(value)} />
                    </div>
                </div>
            </div>

            <div className="phishing-step3__role-list-header">
                <div className="phishing-step3__label">
                    Select a group
                    {filteredGroups.length > 0 && (
                        <>
                            <span className="phishing-step3__dot"> </span>
                            <span className="phishing-step3__total-count">
                                ({filteredGroups.length} Results)
                            </span>
                        </>
                    )}
                    {totalUsersCount > 0 && (
                        <>
                            <span className="phishing-step3__dot"> · </span>
                            <span className="phishing-step3__total-count">
                                {totalUsersCount} users selected
                            </span>
                        </>
                    )}
                    <span className="required"> * </span>
                </div>
                <button className="phishing-step3__select-all" onClick={() => setAllGroupsSelected(!allGroupsSelected)}>
                    {(filteredGroups.length > 0 && selectedGroups.length === filteredGroups.length) ? "Deselect all" : "Select all"}
                </button>
            </div>

            <div className="phishing-step3__role-list">
                {selectedProperties.length === 0 && !allSelected ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        Please select a property to see groups.
                    </div>
                ) : filteredGroups.length === 0 ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        No groups found.
                    </div>
                ) : (
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                        {filteredGroups.map((group) => (
                            <RecipientItem
                                key={group.value}
                                title={group.label}
                                description={`${group.count} users`}
                                iconType="recipientGroup"
                                isChecked={selectedGroups.some(g => g.value === group.value)}
                                onToggle={() => handleGroupToggle(group)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GroupRecipients;